import { formatMoney, resolveCurrencyCode } from '@/lib/money'
import type { RevenueBucket } from '../../services/analyticsService'

function tickIndices(count: number, maxTicks: number): number[] {
  if (count <= 0) return []
  if (count <= maxTicks) return Array.from({ length: count }, (_, i) => i)
  const step = (count - 1) / (maxTicks - 1)
  const indices = Array.from({ length: maxTicks }, (_, i) => Math.round(i * step))
  return [...new Set(indices)]
}

function compactDateLabel(dateKey: string, totalDays: number): string {
  const d = new Date(`${dateKey}T12:00:00.000Z`)
  if (totalDays > 30) {
    return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'UTC' })
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/** Nice Y-axis ceiling and evenly spaced ticks (includes 0). */
function buildYScale(maxRevenue: number) {
  const dataMax = Math.max(maxRevenue, 0)
  if (dataMax === 0) {
    return { yMax: 1, ticks: [0, 0.25, 0.5, 0.75, 1] as number[] }
  }

  const magnitude = 10 ** Math.floor(Math.log10(dataMax))
  const normalized = dataMax / magnitude
  let niceMax = magnitude
  if (normalized <= 1) niceMax = magnitude
  else if (normalized <= 2) niceMax = 2 * magnitude
  else if (normalized <= 5) niceMax = 5 * magnitude
  else niceMax = 10 * magnitude
  if (niceMax < dataMax) niceMax *= 2

  const divisions = 4
  const ticks = Array.from({ length: divisions + 1 }, (_, i) =>
    Math.round(((niceMax * i) / divisions) * 100) / 100,
  )
  return { yMax: niceMax, ticks }
}

function formatAxisMoney(amount: number, code: string): string {
  if (!Number.isFinite(amount)) return '—'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: amount >= 100 ? 0 : amount >= 10 ? 1 : 2,
    }).format(amount)
  } catch {
    return formatMoney(amount, code)
  }
}

type RevenueBarChartProps = {
  buckets: RevenueBucket[]
  currency: string
}

export function RevenueBarChart({ buckets, currency }: RevenueBarChartProps) {
  const code = resolveCurrencyCode(currency)
  const dataMax = Math.max(...buckets.map((b) => b.revenue), 0)
  const { yMax, ticks } = buildYScale(dataMax)
  const maxDateTicks = buckets.length > 30 ? 8 : buckets.length > 14 ? 7 : buckets.length
  const dateTicks = tickIndices(buckets.length, maxDateTicks)
  const plotHeight = 176

  return (
    <div className="pb-1" role="img" aria-label="Revenue over time bar chart">
      <div className="flex gap-2 sm:gap-3">
        <div
          className="relative w-[3.25rem] shrink-0 sm:w-[4.25rem]"
          style={{ height: plotHeight }}
          aria-hidden
        >
          {ticks.map((tick) => {
            const bottomPct = yMax > 0 ? (tick / yMax) * 100 : 0
            return (
              <span
                key={tick}
                className="absolute right-0 -translate-y-1/2 text-[10px] font-medium leading-none tabular-nums text-foreground/80 sm:text-[11px]"
                style={{ bottom: `${bottomPct}%` }}
              >
                {formatAxisMoney(tick, code)}
              </span>
            )
          })}
        </div>

        <div className="relative min-w-0 flex-1">
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: plotHeight }}
            aria-hidden
          >
            {ticks.map((tick) => {
              const bottomPct = yMax > 0 ? (tick / yMax) * 100 : 0
              return (
                <div
                  key={tick}
                  className="absolute inset-x-0 border-t border-border/45"
                  style={{ bottom: `${bottomPct}%` }}
                />
              )
            })}
          </div>

          <div className="relative flex items-end gap-0.5 sm:gap-1" style={{ height: plotHeight }}>
            {buckets.map((bucket) => {
              const heightPct =
                bucket.revenue > 0 && yMax > 0 ? Math.max(2, (bucket.revenue / yMax) * 100) : 0
              const valueLabel = `${bucket.label}: ${formatMoney(bucket.revenue, code)}`
              return (
                <div key={bucket.date} className="flex h-full min-w-0 flex-1 flex-col justify-end">
                  <div
                    className="w-full rounded-t bg-primary/85 transition-all hover:bg-primary"
                    style={{ height: `${heightPct}%` }}
                    title={valueLabel}
                    aria-label={valueLabel}
                  />
                </div>
              )
            })}
          </div>

          <div className="relative mt-3 h-7 overflow-visible border-t border-border/70 px-1 pt-2 sm:px-2">
            {dateTicks.map((index) => {
              const bucket = buckets[index]
              if (!bucket) return null
              const isFirst = index === 0
              const isLast = index === buckets.length - 1
              const leftPct = ((index + 0.5) / buckets.length) * 100
              const label = compactDateLabel(bucket.date, buckets.length)
              return (
                <span
                  key={bucket.date}
                  className={`absolute whitespace-nowrap text-xs font-medium text-foreground/80 ${
                    isFirst ? 'left-0' : isLast ? 'right-0' : '-translate-x-1/2'
                  }`}
                  style={isFirst || isLast ? undefined : { left: `${leftPct}%` }}
                >
                  {label}
                </span>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

type StatusBarListProps = {
  items: { status: string; count: number }[]
}

export function StatusBarList({ items }: StatusBarListProps) {
  const max = Math.max(...items.map((i) => i.count), 1)
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">No orders in this period.</p>
  }
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.status}>
          <div className="mb-1 flex items-center justify-between gap-2 text-sm">
            <span className="capitalize text-foreground">{item.status.replace(/_/g, ' ')}</span>
            <span className="tabular-nums text-muted-foreground">{item.count}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-chart-2"
              style={{ width: `${Math.max(4, (item.count / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}
